import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import AuthLayout from './AuthLayout'
import * as yup from 'yup'
import { yupResolver } from '@hookform/resolvers/yup'
import { FormInput, VerticalForm } from '../../components/Form'
import { registerUser, verifyOtp } from '../../store/user/userThunk' // ✅ Add verifyOtp thunk
import { useDispatch } from 'react-redux'
import { registerResetAuth, storeEmail } from '../../store/user/userSlice'
import { toast } from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'
import EmailVerification from './VerifyEmail'

const schemaResolver = yupResolver(
    yup.object().shape({
        yourName: yup
            .string()
            .required('Please enter your name')
            .min(3, 'Name must be at least 3 characters')
            .max(20, 'Name must be at most 20 characters')
            .matches(/^[a-zA-Z_]+$/, 'Your name can only contain letters and underscores'),
        // .matches(/^[a-zA-Z0-9_]+$/, 'Your name can only contain letters, numbers, and underscores'),
        email: yup
            .string()
            .required('Please enter Email')
            .email('Please enter a valid Email'),
        password: yup
            .string()
            .required('Please enter Password')
            .min(8, 'Password must be at least 8 characters')
            .max(32, 'Password must be at most 32 characters')
            .matches(/[a-z]/, 'Password must contain at least one lowercase letter')
            .matches(/[A-Z]/, 'Password must contain at least one uppercase letter')
            .matches(/\d/, 'Password must contain at least one number')
            .matches(/[!@#$%^&*(),.?":{}|<>]/, 'Password must contain at least one special character'),

        checkbox: yup
            .boolean()
            .oneOf([true], 'You must agree before submitting'),
    })
)

const BottomLink = () => (
    <div className="w-full text-center">
        <p className="text-gray-500 leading-6 text-base">
            Already have an account?{' '}
            <Link to="/auth/sign-in" className="text-primary font-semibold ms-1">
                Log In
            </Link>
        </p>
    </div>
)

const Register = () => {
    const [otpVerified, setOtpVerified] = useState(false);
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [isOtpStep, setIsOtpStep] = useState(false)
    const [userEmail, setUserEmail] = useState('')
    const [code, setCode] = useState(new Array(6).fill(""));

    const onSubmit = async (data) => {
        await dispatch(registerUser(data))
            .unwrap()
            .then(() => {
                dispatch(storeEmail(data.email));
                setUserEmail(data.email);
                setIsOtpStep(true);
            })
            .catch((error) => {
                // toast.error(error || "Registration failed");
                dispatch(registerResetAuth());
            });
    };

    const handleOtpVerify = async (e) => {
        await dispatch(verifyOtp({ email: userEmail, otp: code.join("") }))
            .unwrap()
            .then(() => {
                // toast.success("Account verified! Please login."s);
                // dispatch(registerResetAuth());
                navigate('/auth/sign-in');
            })
            .catch((error) => {
                toast.error(error || "OTP verification failed");
            });
    };

    return (
        <AuthLayout
            authTitle={isOtpStep ? "" : "Create Your Account"}
            helpText={isOtpStep ? "" : "Don't have an account? Create your account, it takes less than a minute."}
            isOtpStep={isOtpStep}
            bottomLinks={<BottomLink />}
        >
            {!isOtpStep ? (
                <VerticalForm onSubmit={onSubmit} resolver={schemaResolver}  >
                    <FormInput
                        name="yourName"
                        type="text"
                        label="Your Name"
                        labelClassName="block text-sm font-medium mb-1 text-gray-600"
                        className="py-2 px-4 leading-6 block w-full border-gray-300 rounded text-sm focus:border-gray-300 focus:ring-0"
                        containerClass="mb-4"
                        placeholder="Your Name"
                        required
                    />
                    <FormInput
                        name="email"
                        type="email"
                        label="Email"
                        labelClassName="block text-sm font-medium mb-1 text-gray-600"
                        className="py-2 px-4 leading-6 block w-full border-gray-300 rounded text-sm focus:border-gray-300 focus:ring-0"
                        containerClass="mb-4"
                        placeholder="Your Email"
                        required
                    />
                    <FormInput
                        label="Password"
                        type="password"
                        name="password"
                        placeholder="Enter your password"
                        className="py-2 px-4 leading-6 block w-full border-gray-300 rounded text-sm focus:border-gray-300 focus:ring-0"
                        containerClass="mb-4"
                        labelClassName="block text-sm font-medium mb-1 text-gray-600"
                        required
                    />
                    <FormInput
                        label="I agree to the Terms & Conditions"
                        type="checkbox"
                        name="checkbox"
                        className="h-4 w-4 text-blue-600 border-gray-300 rounded"
                        containerClass="flex items-center mb-4"
                        labelClassName="ms-2 text-sm font-medium text-gray-700"
                    />

                    <div className="mb-0 text-center">
                        <button
                            className="w-full bg-primary text-white font-medium leading-6 text-center align-middle select-none py-2 px-4 text-base rounded-md transition-all hover:shadow-lg hover:shadow-primary/30"
                            type="submit"
                        >
                            Register
                        </button>
                    </div>
                </VerticalForm>
            ) : (isOtpStep && !otpVerified) ? (
                <EmailVerification handleOtpVerify={handleOtpVerify} userEmail={userEmail} setCode={setCode} code={code} />
            ) : (
                <div className="bg-white p-6 rounded-lg shadow-lg text-center">
                    <h2 className="text-2xl font-semibold mb-2">Success! 🎉</h2>
                    <p>Your email has been verified. You can now log in.</p>
                </div>
            )}
        </AuthLayout>
    );
};

export default Register;
